import type { ApolloClient } from "@apollo/client";
import { gql, useLazyQuery, useMutation } from "@apollo/client";

import type {
  ICaaCustomerAddressCreateMutation,
  ICaaCustomerAddressDeleteMutation,
  ICaaCustomerAddressUpdateMutation,
  ICaaCustomerEmailMarketingSubscribeMutation,
  ICaaCustomerEmailMarketingUnsubscribeMutation,
  ICaaCustomerQuery,
  ICaaCustomerUpdateMutation,
} from "./customer-account-gql";
import {
  caaCustomerAddressCreateMutation,
  caaCustomerAddressDeleteMutation,
  caaCustomerAddressUpdateMutation,
  caaCustomerEmailMarketingSubscribeMutation,
  caaCustomerEmailMarketingUnsubscribeMutation,
  caaCustomerQuery,
  caaCustomerUpdateMutation,
} from "./customer-account-gql";

interface IUseCustomerQueryCaaProps {
  /**
   * 스토어별 Customer Account API Apollo client (CAA endpoint로 라우팅)
   * 스토어마다 apollo client 인스턴스가 다르므로 호출부에서 주입
   */
  client: ApolloClient<object>;
}

/**
 * Customer Account API용 customer 쿼리/뮤테이션 바인딩.
 * 호출하는 곳(fetchCaaCustomer({ onCompleted, onError }) 등)에서 처리
 */
export default function useCustomerQueryCaa({ client }: IUseCustomerQueryCaaProps) {
  // 회원 정보 조회
  const [fetchCaaCustomer, { loading: fetchCaaCustomerLoading }] = useLazyQuery<ICaaCustomerQuery>(gql(caaCustomerQuery.query), {
    client,
    fetchPolicy: "no-cache",
  });

  // 회원 정보 수정
  const [updateCaaCustomer, { loading: updateCaaCustomerLoading }] = useMutation<ICaaCustomerUpdateMutation>(
    gql(caaCustomerUpdateMutation.query),
    { client },
  );

  // 배송지 생성
  const [createCaaAddress, { loading: createCaaAddressLoading }] = useMutation<ICaaCustomerAddressCreateMutation>(
    gql(caaCustomerAddressCreateMutation.query),
    { client },
  );

  // 배송지 수정 (CAA는 defaultAddress 옵션도 같이 받음)
  const [editCaaAddress, { loading: editCaaAddressLoading }] = useMutation<ICaaCustomerAddressUpdateMutation>(
    gql(caaCustomerAddressUpdateMutation.query),
    { client },
  );

  // 배송지 삭제
  const [deleteCaaAddress, { loading: deleteCaaAddressLoading }] = useMutation<ICaaCustomerAddressDeleteMutation>(
    gql(caaCustomerAddressDeleteMutation.query),
    { client },
  );

  // 이메일 마케팅 구독
  const [subscribeCaaEmailMarketing, { loading: subscribeCaaEmailMarketingLoading }] =
    useMutation<ICaaCustomerEmailMarketingSubscribeMutation>(gql(caaCustomerEmailMarketingSubscribeMutation.query), {
      client,
    });

  // 이메일 마케팅 구독 해지
  const [unsubscribeCaaEmailMarketing, { loading: unsubscribeCaaEmailMarketingLoading }] =
    useMutation<ICaaCustomerEmailMarketingUnsubscribeMutation>(gql(caaCustomerEmailMarketingUnsubscribeMutation.query), { client });

  return {
    fetchCaaCustomer,
    fetchCaaCustomerLoading,
    updateCaaCustomer,
    updateCaaCustomerLoading,
    createCaaAddress,
    createCaaAddressLoading,
    editCaaAddress,
    editCaaAddressLoading,
    deleteCaaAddress,
    deleteCaaAddressLoading,
    subscribeCaaEmailMarketing,
    subscribeCaaEmailMarketingLoading,
    unsubscribeCaaEmailMarketing,
    unsubscribeCaaEmailMarketingLoading,
  };
}
